// MCHD Room Scheduler - User Avatar Component
// Created: 07-10-2025 14:01:32
// Purpose: Display user avatar with initials based on name

import React from 'react';
import { Avatar, Tooltip } from '@mantine/core';

/**
 * Props for UserAvatar component
 */
export interface UserAvatarProps {
  /** Full name of the user */
  name: string;
  /** User role (ADMIN, COORDINATOR, DOCTOR) */
  role?: string;
  /** Size of the avatar */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;
  /** Optional color override */
  color?: string;
  /** Optional click handler */
  onClick?: () => void;
  /** Optional CSS class name */
  className?: string;
}

/**
 * Get user initials from full name
 * 
 * Business Rules (from UX Touchpoint Spec):
 * - "SA" for System Administrator (exact match)
 * - Otherwise: first letter of first name + first letter of last name
 * - All uppercase
 * 
 * @param name - Full name of the user
 * @returns Two-letter initials (e.g., "JD", "SA")
 * 
 * @example
 * getInitials("John Doe") // Returns "JD"
 * getInitials("System Administrator") // Returns "SA"
 * getInitials("Dr. Ahmed") // Returns "DA"
 * 
 * Created: 07-10-2025 14:01:32
 */
export function getInitials(name: string): string {
  // Exact match for System Administrator per spec
  if (name.trim() === 'System Administrator') {
    return 'SA';
  }

  // Split name into parts and filter empty strings
  const parts = name.trim().split(' ').filter(part => part.length > 0);

  if (parts.length === 0) {
    return '??'; // Fallback for empty names
  }

  if (parts.length === 1) {
    // Single name: use first two letters or first letter twice
    return parts[0].length >= 2 
      ? parts[0].substring(0, 2).toUpperCase()
      : parts[0][0].toUpperCase();
  }

  // Multiple parts: first letter of first + first letter of last
  const firstInitial = parts[0][0];
  const lastInitial = parts[parts.length - 1][0];
  
  return `${firstInitial}${lastInitial}`.toUpperCase();
}

/**
 * UserAvatar Component
 * 
 * Displays a user avatar with automatically generated initials.
 * Follows UX Touchpoint Spec requirements for avatar display.
 * 
 * Features:
 * - Automatic initials generation from name
 * - Special case for "System Administrator" → "SA"
 * - Tooltip showing full name on hover
 * - Customizable size and color
 * - Clickable with optional handler
 * 
 * @component
 * @example
 * ```tsx
 * <UserAvatar name="John Doe" role="DOCTOR" size="md" />
 * <UserAvatar name="System Administrator" role="ADMIN" size="lg" />
 * ```
 * 
 * Created: 07-10-2025 14:01:32
 * Complies: @core-development-philosophy.mdc, @typescript-quality.mdc
 */
export const UserAvatar: React.FC<UserAvatarProps> = ({
  name,
  role,
  size = 'md',
  color,
  onClick,
  className,
}) => {
  const initials = getInitials(name);
  
  // Color mapping based on role (optional enhancement)
  const defaultColor = color || (
    role === 'ADMIN' ? 'red' :
    role === 'COORDINATOR' ? 'blue' :
    role === 'DOCTOR' ? 'green' :
    'gray'
  );

  return (
    <Tooltip label={name} position="bottom" withArrow>
      <Avatar
        size={size}
        radius="xl"
        color={defaultColor}
        {...(onClick && { onClick })}
        {...(className && { className })}
        style={{ 
          cursor: onClick ? 'pointer' : 'default',
          fontWeight: 600,
          letterSpacing: '0.5px',
        }}
        aria-label={`User avatar for ${name}`}
      >
        {initials}
      </Avatar>
    </Tooltip>
  );
};

export default UserAvatar;

